<template>
	<div
		v-if="integrationActions?.length > 0"
		class="adapterCode-actions-dropdown"
	>
		<SuiButton
			variant="secondary-outlined"
			class="adapterCode-actions-dropdown__button"
			:disabled="integrationActionLoading"
			:is-loading="integrationActionLoading"
			@click="showDropdown()"
		>
			<SuiIcon name="icon-adapterCode-16" />

			{{ getActionButtonLabel }}

			<IntegrationActionsBubble
				v-if="syncStatus !== SyncStatus.STATUS_DEFAULT"
				:sync-status="syncStatus"
				:error-count="errorCount"
			/>
		</SuiButton>
		<VDropdown :shown="isOpen" :distance="5" @auto-hide="hideDropdown">
			<template #popper>
				<div class="adapterCode-actions-dropdown__dropdown">
					<a
						v-for="item in integrationActions"
						:key="item.type"
						class="adapterCode-actions-dropdown__dropdown-item"
						:class="classAddons(item)"
						:href="item.href"
						@click="handleAction(item)"
					>
						<SuiIcon :name="item.icon" />

						{{ item.title }}

						<IntegrationActionsBubble
							v-if="
								item.type ===
								SyncActions.SYNC_ACTION_SHOW_ERRORS
							"
							:error-count="errorCount"
							:sync-status="syncStatus"
						/>
					</a>
				</div>
			</template>
		</VDropdown>
	</div>
</template>

<script setup lang="ts">
import { SuiButton, SuiIcon } from '@developers/scoro-ui';
import { computed, ref } from 'vue';
import { SyncActions } from '@/constants/constants';
import {
	type IntegrationActionItem,
	type SyncActionType,
	SyncStatus,
} from '../types';
import IntegrationActionsBubble from '../components/IntegrationActionsBubble.vue';
import { Constants } from '@/constants';
import {
	type IntegrationAction,
	useProjectDetailsHeaderIntegrationActionListQuery,
	useIntegrationActionRunMutation,
} from '@/generated/graphql';
import { useTranslation } from '@/composables';
import { useMessage } from '@/features/Message';
import { Monolith } from '@/constants/monolith';

const { t } = useTranslation();

const { addErrorMessage, addSuccessMessage, addWarningMessage } = useMessage();

export type IntegrationActionsDropdownProps = {
	adapterCode: string;
	itemId: string;
	itemType: string;
	enabled: boolean;
};

const { adapterCode, itemId, itemType, enabled } =
	defineProps<IntegrationActionsDropdownProps>();

const { result: integrationActionList, refetch: refetchActionItems } =
	useProjectDetailsHeaderIntegrationActionListQuery(
		{
			input: {
				itemId: String(itemId),
				itemType,
				adapterCode,
			},
		},
		{
			enabled,
		}
	);

const integrationDropdownItems = computed<IntegrationAction[]>(
	() => integrationActionList.value?.IntegrationActionList ?? []
);

const isOpen = ref(false);

const integrationActions = computed<IntegrationActionItem[]>(() =>
	(integrationDropdownItems.value ?? []).map(
		({ type, icon, title, href, syncStatus, errorCount }) => ({
			type: type as SyncActionType,
			icon,
			title,
			href: href ?? undefined,
			syncStatus: Object.values(SyncStatus).find(
				(status) => status === syncStatus
			),
			errorCount: errorCount ?? undefined,
		})
	)
);

const getActionButtonLabel = computed(() => {
	if (adapterCode === Monolith.AdapterCodes.SAGE) {
		return t('sage-actions-button');
	} else {
		return t('quickbooksActionsButton');
	}
});

const errorCount = computed(
	() =>
		integrationActions.value.find(
			(action) =>
				action.type === Constants.SyncActions.SYNC_ACTION_SHOW_ERRORS
		)?.errorCount
);
const syncStatus = computed(
	() =>
		integrationActions.value.find(
			(action) =>
				action.type === Constants.SyncActions.SYNC_ACTION_SHOW_ERRORS ||
				action.type === Constants.SyncActions.SYNC_ACTION_SHOW_SYNC_DATA
		)?.syncStatus ?? SyncStatus.STATUS_DEFAULT
);

const classAddons = (action: IntegrationActionItem) => {
	if (action.type === Constants.SyncActions.SYNC_ACTION_SHOW_ERRORS) {
		return [
			`adapterCode-actions-dropdown__dropdown-item--${action.syncStatus}`,
		];
	}

	if (action.type === Constants.SyncActions.SYNC_ACTION_SHOW_SYNC_DATA) {
		return [`adapterCode-actions-dropdown__dropdown-item--no-click`];
	}

	return [];
};

const {
	mutate: integrationActionRun,
	onDone,
	loading: integrationActionLoading,
} = useIntegrationActionRunMutation({});

const hideDropdown = () => {
	isOpen.value = false;
};

const handleAction = (actionItem: IntegrationActionItem) => {
	if (
		!!actionItem?.href ||
		actionItem.type === Constants.SyncActions.SYNC_ACTION_SHOW_SYNC_DATA
	) {
		return;
	}
	hideDropdown();
	integrationActionRun({
		input: {
			adapterCode,
			itemId,
			itemType,
			action: actionItem.type,
		},
	});
};

const showDropdown = () => {
	isOpen.value = true;
};

onDone((resp) => {
	if (resp.data?.IntegrationActionRun?.type === 'success') {
		addSuccessMessage({
			message: String(resp.data?.IntegrationActionRun?.message),
		});
	} else if (resp.data?.IntegrationActionRun?.type === 'warning') {
		addWarningMessage({
			message: String(resp.data?.IntegrationActionRun?.message),
		});
	} else {
		addErrorMessage({
			message: String(resp.data?.IntegrationActionRun?.message),
		});
	}
	refetchActionItems();
});
</script>

<style lang="scss" scoped>
.adapterCode-actions-dropdown {
	&__dropdown {
		padding: 0.75rem 0;
	}

	&__button {
		display: flex;
		gap: 0.5rem;

		.sui-icon {
			margin: 0;
		}
	}

	&__dropdown-item {
		position: relative;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.875rem 2rem;
		cursor: pointer;

		&:hover {
			background: $neutral-20;
			color: inherit;
		}

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			width: 0.5rem;
			height: 100%;
			content: '';
		}

		&--status-warning {
			background: $orange-10;

			&:hover {
				background: $orange-20;
			}

			&::before {
				background: $orange-50;
			}
		}

		&--status-error {
			background: $red-10;

			&:hover {
				background: $red-20;
			}

			&::before {
				background: $red-50;
			}
		}

		&--no-click {
			cursor: default;

			&:hover {
				background: none;
			}
		}
	}
}
</style>
